import { REPORTS_TEXT } from '@/texts/catalog/reports';
import { formatReportsMetricValue } from '@/utils/reportsMetrics';
import { buildFileTimestampSegment } from '@/utils/fileDownload';

export const REPORTS_SALES_FALLBACK_REPORT_CODE = 'sales_done';
export const SALES_PROFITABILITY_REPORT_CODE = 'sales_profitability';
export const REPORTS_TABLE_EXPORTABLE_CODES = ['sales_done', 'sales_invoiced', 'sales_simple_notes', SALES_PROFITABILITY_REPORT_CODE];

const ALLOWED_REPORT_CODES = [
  'sales_done',
  'sales_invoiced',
  'sales_simple_notes',
  SALES_PROFITABILITY_REPORT_CODE,
];

const METRIC_VISUAL_BY_CODE = {
  total_businesses: { icon: 'fa-briefcase', iconBg: 'rgba(0,112,205,0.14)', iconColor: '#0070cd' },
  projected_total: { icon: 'fa-line-chart', iconBg: 'rgba(59,212,46,0.14)', iconColor: '#1f9b43' },
  average_business: { icon: 'fa-calculator', iconBg: 'rgba(0,112,205,0.12)', iconColor: '#0f5da8' },
  close_percentage: { icon: 'fa-check-circle', iconBg: 'rgba(250,204,21,0.2)', iconColor: '#b45309' },
  incomes_total: { icon: 'fa-line-chart', iconBg: 'rgba(59,212,46,0.14)', iconColor: '#1f9b43' },
  expenses_total: { icon: 'fa-credit-card', iconBg: 'rgba(14,116,144,0.15)', iconColor: '#0e7490' },
  budget_total: { icon: 'fa-thermometer-three-quarters', iconBg: 'rgba(239,68,68,0.14)', iconColor: '#b91c1c' },
  utility_total: { icon: 'fa-area-chart', iconBg: 'rgba(59, 212, 46, 0.12)', iconColor: '#3bd42e' },
};

const FALLBACK_METRIC_VISUALS = [
  { icon: 'fa-bar-chart', iconBg: 'rgba(0,112,205,0.14)', iconColor: '#0070cd' },
  { icon: 'fa-line-chart', iconBg: 'rgba(59,212,46,0.14)', iconColor: '#1f9b43' },
  { icon: 'fa-calculator', iconBg: 'rgba(0,112,205,0.12)', iconColor: '#0f5da8' },
  { icon: 'fa-pie-chart', iconBg: 'rgba(250,204,21,0.2)', iconColor: '#b45309' },
];

export const REPORTS_SALES_TEXT = REPORTS_TEXT.sales;

export const requireReportsSalesTextValue = (value, keyPath) => {
  const normalized = String(value ?? '').trim();
  if (normalized === '') {
    throw new Error(`Missing reports sales text value: ${keyPath}`);
  }

  return normalized;
};

const reportExportSegments = () => {
  const source = REPORTS_SALES_TEXT.page.exportFileSegments;

  return {
    sales_done: requireReportsSalesTextValue(source.sales_done, 'reports.sales.page.exportFileSegments.sales_done'),
    sales_invoiced: requireReportsSalesTextValue(source.sales_invoiced, 'reports.sales.page.exportFileSegments.sales_invoiced'),
    sales_simple_notes: requireReportsSalesTextValue(source.sales_simple_notes, 'reports.sales.page.exportFileSegments.sales_simple_notes'),
    sales_profitability: requireReportsSalesTextValue(source.sales_profitability, 'reports.sales.page.exportFileSegments.sales_profitability'),
    default: requireReportsSalesTextValue(source.default, 'reports.sales.page.exportFileSegments.default'),
  };
};

export const normalizeReportsSalesReportCode = (value) => {
  const normalized = String(value || '').trim();

  if (ALLOWED_REPORT_CODES.includes(normalized)) {
    return normalized;
  }

  return REPORTS_SALES_FALLBACK_REPORT_CODE;
};

export const normalizeReportsMetricCards = (rawCards = []) => {
  return (Array.isArray(rawCards) ? rawCards : []).map((card, index) => {
    const normalizedCode = String(card?.code || '').trim();
    const visual = METRIC_VISUAL_BY_CODE[normalizedCode]
      || FALLBACK_METRIC_VISUALS[index % FALLBACK_METRIC_VISUALS.length];

    return {
      title: String(card?.name ?? '').trim(),
      value: formatReportsMetricValue(card?.value, card?.format),
      valueMeta: String(card?.description ?? '').trim(),
      icon: visual.icon,
      iconBg: visual.iconBg,
      iconColor: visual.iconColor,
    };
  });
};

export const normalizeReportsTablePayload = (payloadTable) => {
  return {
    headers: Array.isArray(payloadTable?.headers) ? payloadTable.headers : [],
    rows: Array.isArray(payloadTable?.rows) ? payloadTable.rows : [],
    period_totals: Array.isArray(payloadTable?.period_totals) ? payloadTable.period_totals : [],
    grand_total: Number(payloadTable?.grand_total || 0),
    period_total_element_ids: Array.isArray(payloadTable?.period_total_element_ids)
      ? payloadTable.period_total_element_ids
      : [],
    grand_total_element_ids: Array.isArray(payloadTable?.grand_total_element_ids)
      ? payloadTable.grand_total_element_ids
      : [],
  };
};

export const buildReportsSalesExportFileName = (reportCode, extension) => {
  const normalizedReportCode = normalizeReportsSalesReportCode(reportCode);
  const normalizedExtension = String(extension || '').trim().toLowerCase();
  const segments = reportExportSegments();
  const baseSegment = segments[normalizedReportCode] || segments.default;

  return `${baseSegment}_${buildFileTimestampSegment()}.${normalizedExtension}`;
};

export const buildReportsSalesHeaderActions = () => {
  return [
    {
      key: 'excel',
      label: requireReportsSalesTextValue(REPORTS_SALES_TEXT.page.headerActions.excel, 'reports.sales.page.headerActions.excel'),
      icon: 'fa-file-excel-o',
      variant: 'secondary',
    },
    {
      key: 'pdf',
      label: requireReportsSalesTextValue(REPORTS_SALES_TEXT.page.headerActions.pdf, 'reports.sales.page.headerActions.pdf'),
      icon: 'fa-file-pdf-o',
      variant: 'secondary',
    },
  ];
};

export const buildReportsSalesProfitabilityTab = () => {
  return {
    key: requireReportsSalesTextValue(REPORTS_SALES_TEXT.tabs.profitability.key, 'reports.sales.tabs.profitability.key'),
    label: requireReportsSalesTextValue(REPORTS_SALES_TEXT.tabs.profitability.label, 'reports.sales.tabs.profitability.label'),
    headerTitle: requireReportsSalesTextValue(REPORTS_SALES_TEXT.tabs.profitability.headerTitle, 'reports.sales.tabs.profitability.headerTitle'),
    description: requireReportsSalesTextValue(REPORTS_SALES_TEXT.tabs.profitability.description, 'reports.sales.tabs.profitability.description'),
    isDefault: false,
  };
};

export const normalizeReportsSalesTabs = (rawTabs = []) => {
  const titlePrefix = requireReportsSalesTextValue(REPORTS_SALES_TEXT.page.tabHeaderPrefix, 'reports.sales.page.tabHeaderPrefix');

  return (Array.isArray(rawTabs) ? rawTabs : [])
    .map((tab) => {
      const code = normalizeReportsSalesReportCode(tab?.code);
      const name = String(tab?.name ?? '').trim();
      const description = String(tab?.description ?? '').trim();

      if (code === '' || name === '') {
        return null;
      }

      return {
        key: code,
        label: name,
        headerTitle: `${titlePrefix} ${name}`,
        description,
        isDefault: tab?.is_default === true || Number(tab?.is_default) === 1,
      };
    })
    .filter(Boolean);
};
